import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../ui/dialog";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerFooter, DrawerClose } from "../../ui/drawer";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { AlertCircle, X } from 'lucide-react';

const UserFormModal = ({ isOpen, onClose, onSubmit, user = null }) => {
  const { t } = useTranslation();
  const isEditing = !!user;
  
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    username: "", 
    email: "", 
    building_code: "X", 
    points: "0", 
    role: "1", 
    status: "1"
  });
  
  const [error, setError] = useState("");
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

  // Check screen size
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 640);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // When editing, populate form with user data
  useEffect(() => {
    // Only reset form when modal opens
    if (isOpen) {
      if (isEditing && user) {
        // Make sure values are converted to strings for the Select components
        setFormData({
          first_name: user.first_name,
          last_name: user.last_name,
          username: user.username,
          email: user.email,
          building_code: "X",
          points: String(user.points),
          role: String(user.role),
          status: String(user.status),
          // Don't include password when editing
        });
      } else {
        // Reset form when adding a new user
        setFormData({
          first_name: "",
          last_name: "",
          username: "", 
          email: "", 
          building_code: "X", 
          points: "0", 
          role: "1", 
          status: "1"
        });
      }
      
      // Clear any previous errors
      setError("");
    }
  }, [isOpen, isEditing, user]); 

  const handleSubmit = async () => {
    // Basic validation
    if (!formData.username || !formData.email || !formData.first_name || !formData.last_name) {
      setError("Please fill in all required fields");
      return;
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError("Please enter a valid email address");
      return;
    }
    
    try {
      // If editing, pass the user ID
      const submitData = isEditing ? { id: user.id, ...formData } : formData;
      await onSubmit(submitData);
      setError("");
      onClose();
    } catch (error) {
      setError(t('management.parking.form.userError', { message: error.message || t('parking.unknown') }));
    }
  };

  // Helper function to get role label
  const getRoleLabel = (role) => {
    return role === "2" ? "Building Manager" : "Resident";
  };
  
  // Helper function to get status label
  const getStatusLabel = (status) => {
    return status === "1" ? "Active" : "Inactive";
  };

  // Shared content for both mobile and desktop
  const ModalContent = () => (
    <>
      <div className="space-y-4">
        {error && (
          <div className="text-sm font-medium text-red-500 bg-red-50 dark:bg-red-900/20 p-3 rounded flex items-start gap-2">
            <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium mb-1.5" htmlFor="first_name">
            First Name <span className="text-red-500">*</span>
          </label>
          <Input
            id="first_name"
            value={formData.first_name}
            onChange={(e) => setFormData({...formData, first_name: e.target.value})}
            placeholder="John"
            className="h-10"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1.5" htmlFor="last_name">
            Last Name <span className="text-red-500">*</span>
          </label>
          <Input
            id="last_name"
            value={formData.last_name}
            onChange={(e) => setFormData({...formData, last_name: e.target.value})}
            placeholder="Doe"
            className="h-10"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1.5" htmlFor="username">
            Username <span className="text-red-500">*</span>
          </label>
          <Input
            id="username"
            value={formData.username}
            onChange={(e) => setFormData({...formData, username: e.target.value})}
            placeholder="john.doe"
            className="h-10"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium mb-1.5" htmlFor="email">
            Email <span className="text-red-500">*</span>
          </label>
          <Input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({...formData, email: e.target.value})}
            placeholder="john@example.com"
            className="h-10"
          />
        </div>


      
        
        <div>
          <label className="block text-sm font-medium mb-1.5" htmlFor="role">
            Role
          </label>
          <Select 
            value={formData.role} 
            onValueChange={(value) => setFormData({...formData, role: value})}
          >
            <SelectTrigger id="role" className="w-full h-10">
              <SelectValue>
                {getRoleLabel(formData.role)}
              </SelectValue>
            </SelectTrigger>
            <SelectContent position={isMobile ? "popper" : "item-aligned"}>
              <SelectItem value="1">Resident</SelectItem>
              <SelectItem value="2">Building Manager</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <label className="block text-sm font-medium mb-1.5" htmlFor="status">
            Status
          </label>
          <Select 
            value={formData.status} 
            onValueChange={(value) => setFormData({...formData, status: value})}
          >
            <SelectTrigger id="status" className="w-full h-10">
              <SelectValue>
                {getStatusLabel(formData.status)}
              </SelectValue>
            </SelectTrigger>
            <SelectContent position={isMobile ? "popper" : "item-aligned"}>
              <SelectItem value="1">Active</SelectItem>
              <SelectItem value="0">Inactive</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </>
  );

  // Mobile drawer version
  if (isMobile) {
    return (
<Drawer 
  open={isOpen} 
  onOpenChange={(open) => {
    if (!open) onClose();
  }}
>        <DrawerContent className="px-4 pt-4 pb-6 max-h-[90vh] overflow-y-auto">
          <DrawerHeader className="p-0 mb-4">
            <DrawerTitle className="text-lg font-semibold">
              {isEditing ? 'Edit User' : 'Add New User'}
            </DrawerTitle>
            <DrawerClose asChild>
              <Button variant="ghost" size="icon" className="absolute right-2 top-2 h-8 w-8">
                <X className="h-4 w-4" />
              </Button>
            </DrawerClose>
          </DrawerHeader>
          
          <ModalContent />
          
          <DrawerFooter className="px-0 pt-6">
            <Button 
              onClick={handleSubmit}
              className="bg-primary text-white hover:bg-primary/90 w-full h-10"
            >
              {isEditing ? 'Save Changes' : 'Add User'}
            </Button>
            <Button 
              variant="outline" 
              onClick={onClose}
              className="w-full h-10"
            >
              Cancel
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    );
  }

  // Desktop dialog version
  return (
<Dialog 
  open={isOpen} 
  onOpenChange={(open) => {
    if (!open) onClose();
  }}
>      <DialogContent className="sm:max-w-[425px] w-[95vw] max-h-[90vh] overflow-y-auto p-4 sm:p-6">
        <DialogHeader className="mb-4 relative">
          <DialogTitle className="text-lg sm:text-xl pr-8">
            {isEditing ? 'Edit User' : 'Add New User'}
          </DialogTitle>
          <button 
            onClick={onClose}
            className="absolute right-0 top-0 p-1 rounded-full hover:bg-neutral-100 dark:hover:bg-neutral-800"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </DialogHeader>

        <ModalContent />

        <DialogFooter className="mt-6 flex-col sm:flex-row gap-3 sm:gap-2">
          <Button 
            variant="outline" 
            onClick={onClose}
            className="w-full sm:w-auto h-10"
          >
            Cancel
          </Button>
          <Button 
            className="bg-primary text-white hover:bg-primary/90 w-full sm:w-auto h-10"
            onClick={handleSubmit}
          >
            {isEditing ? 'Save Changes' : 'Add User'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UserFormModal;